import type { ComponentType, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import { abaDaCasca, paginaNoSubsistema, type ConfigSubsistemas, type MenuPrincipal } from '@malgoos/shared';
import { NAVEGACAO_MENUS } from './navegacao-menus';

export interface AbaWeb {
  /** Segmento da URL e chave i18n (`<slug>:abas.<chave>`). */
  chave: string;
  /**
   * Permissão de ver a aba. Fase 2 (W31): uma lista vale como "qualquer uma delas" (Financeiro > Índices aparece para
   * quem vê o menu Financeiro, pelo Financeiro ou pelo Contas).
   */
  permissao: string | readonly string[];
  componente: ComponentType;
  /**
   * Fase 2 (W6, L1): aba PROJETADA em outro menu. A tela continua do módulo dono (dado, rotas de API, permissão e tools
   * não mudam); só a casa na navegação muda: `/<menu>/<slug>/<chave>` em vez do menu do módulo. A rota antiga
   * redireciona para a nova (`destinoDaAbaProjetada`). Ex.: Categorias do Financeiro em Cadastros.
   */
  menu?: MenuPrincipal;
  /**
   * Fase 2 (W8): a aba só aparece na navegação com o modo de operação num destes valores (`{ chave: 'compras.modo', em:
   * ['detalhado'] }`). Fora dele a rota continua abrindo pelo link (histórico do fluxo detalhado, só leitura).
   */
  modo?: { chave: string; em: string[] };
  /**
   * Fase 2 (W31): grupo (aba de primeiro nível) no menu que agrupa abas de vários módulos (`NAVEGACAO_MENUS`, Cadastros).
   * Sem grupo, a aba fica no grupo com o nome do módulo.
   */
  grupo?: string;
  /** Fase 2 (W31): posição da aba na navegação do menu (linha única ou grupo); sem ela, a ordem do módulo. */
  posicao?: number;
  /**
   * Fase 2 (W37): só o administrador da plataforma (`usuario.plataforma_admin`) vê a aba, além da permissão. O conteúdo é
   * da instalação, não do tenant (Histórico telas); o servidor confere o mesmo (403 `permissao.plataforma`).
   */
  plataforma?: boolean;
  /**
   * Lote 19 (painel do grupo): só quem vê todas as empresas do workspace e tem o consolidado (`nucleo.consolidado`) vê a
   * aba, além da permissão. O servidor confere o mesmo (403 `permissao.painel_grupo_exige_todas`).
   */
  todasAsEmpresas?: boolean;
  /**
   * U15 F5: aba avançada (configuração fina, conciliação, metas). Com o "modo simples" ligado para a pessoa, some da
   * navegação (`abaVisivel`); a permissão não muda e a rota continua abrindo pelo link.
   */
  avancada?: boolean;
}

/** O que a aba precisa da sessão para decidir se aparece. */
export interface SessaoDaAba {
  pode(permissao: string): boolean;
  modo(chave: string): string | undefined;
  /** Fase 2 (W37): a pessoa é administradora da plataforma; ausente vale como não. */
  plataforma?: boolean;
  /** Lote 19: a pessoa vê todas as empresas e tem o consolidado; ausente vale como não. */
  todasAsEmpresas?: boolean;
  /** U15 F5: modo simples ligado para a pessoa; ausente vale desligado. */
  modoSimples?: boolean;
}

/** Fase 2 (W31): a pessoa pode abrir a aba (uma permissão, ou qualquer uma da lista). */
export function podeVerAba(a: Pick<AbaWeb, 'permissao' | 'plataforma' | 'todasAsEmpresas'>, s: Pick<SessaoDaAba, 'pode' | 'plataforma' | 'todasAsEmpresas'>): boolean {
  // Fase 2 (W37): aba da plataforma só para quem a administra.
  if (a.plataforma && !s.plataforma) return false;
  // Lote 19: aba do grupo só para quem vê todas as empresas com o consolidado.
  if (a.todasAsEmpresas && !s.todasAsEmpresas) return false;
  return typeof a.permissao === 'string' ? s.pode(a.permissao) : a.permissao.some((p) => s.pode(p));
}

/** Fase 2 (W31): permissão da aba como texto ("a ou b"), para o mapa de caminhos. */
export const permissaoDaAba = (a: Pick<AbaWeb, 'permissao' | 'plataforma' | 'todasAsEmpresas'>) =>
  `${typeof a.permissao === 'string' ? a.permissao : a.permissao.join(' ou ')}${a.plataforma ? ' (administrador da plataforma)' : ''}${a.todasAsEmpresas ? ' e nucleo.consolidado (vê todas as empresas)' : ''}`;

/** Fase 2 (W8): aba visível na navegação: permissão e, quando declara, o modo de operação do workspace. */
export function abaVisivel(a: Pick<AbaWeb, 'permissao' | 'modo' | 'plataforma' | 'todasAsEmpresas' | 'avancada'>, s: SessaoDaAba): boolean {
  if (!podeVerAba(a, s)) return false;
  // U15 F5: modo simples esconde as abas avançadas da navegação (a rota segue aberta pelo link).
  if (a.avancada && s.modoSimples) return false;
  if (!a.modo) return true;
  const atual = s.modo(a.modo.chave);
  return atual !== undefined && a.modo.em.includes(atual);
}

/** Fase 2 (W23, K11): o registro em que a ação do módulo aparece (no chatter de qualquer registro). */
export interface PropsAcaoNoRegistro {
  entidade: string;
  registroId: string;
  /** Título do registro (nome, número), quando a tela o informa ao chatter. */
  titulo?: string | null;
}

export interface RotaExtraWeb {
  /** Relativo a /<menu>/<slug>/. Ex.: `parceiro/:id`. */
  caminho: string;
  componente: ComponentType;
}

/**
 * CRM no novo desenho (Fase 2, 26/10): página própria de um sub-sistema na casca nova (Empresas, Contatos, Reuniões/Agenda,
 * Atividades e Comercial do clone do RD). Rota de topo `/<caminho>/*`, fora da `Secao` (sem a barra de abas), com sub-rotas
 * (`crm/empresas/:id`). Na barra lateral da casca nova entra logo depois do Início, pela `posicao`, nos sub-sistemas em que a
 * configuração a põe (val8: página `<slug>.casca-<chave>` da configuração, padrão na `DIVISAO_PADRAO` do shared). Na casca de
 * sempre não aparece no menu; o link continua abrindo (sub-sistema é filtro de menu, quem abre é a permissão).
 */
export interface PaginaCascaWeb {
  /** Rótulo `<slug>:casca.<chave>`. */
  chave: string;
  /** Sem barra no começo nem no fim (`crm/empresas`). */
  caminho: string;
  componente: ComponentType;
  /** Permissão de abrir (uma, ou qualquer uma da lista). */
  permissao: string | readonly string[];
  /**
   * Val1 (papel por sub-sistema): a página (aba `slug.aba`) que esta página da casca mostra. Desmarcada no papel (ou retirada
   * da pessoa), some da barra lateral e da rota, como a aba. Sem ela, só a permissão.
   */
  pagina?: string;
  icone: LucideIcon;
  posicao: number;
  /**
   * Lote crm5 (X2): selo do item na barra lateral (a bolinha das tarefas atrasadas). O componente busca o próprio número e
   * não renderiza nada quando é zero; `compacto` é o trilho de ícones do menu recolhido.
   */
  selo?: ComponentType<{ compacto?: boolean }>;
  /**
   * Lote crm8 (item 5): menu que esta página substitui na barra lateral da casca nova, no sub-sistema em que ela aparece (o
   * "Agenda" sai onde "Reuniões/Agenda" está: uma tela só). Os endereços do menu seguem valendo.
   */
  substituiMenu?: string;
  /**
   * Rodada rlote-2 (SPEC-B1): página que tem rota e permissão próprias mas não entra na barra lateral (a Central de Arquivos,
   * dentro das Configurações do CRM, que abre também para quem não configura o CRM).
   */
  semMenu?: boolean;
}

/**
 * Ajustes do CRM 27/10 (C6, K2, C8): o que o contexto da página já sabe para pré-preencher uma criação (a empresa aberta, o
 * contato aberto, o funil escolhido). Publicado pela página com `useContextoDaCriacao` (nucleo/criacao-rapida.tsx).
 */
export interface ContextoCriacao {
  empresa?: { id: string; nome: string };
  contato?: { id: string; nome: string };
  funilId?: string;
  /** rl0211-2 (VIS-M1): a negociação da página (a reunião ligada a ela); a tarefa nasce dentro dela, como na ficha. */
  negociacao?: { id: string; parceiro_id?: string | null };
  /**
   * crm4 (L2): criação em lote a partir da seleção das listas (Empresas, Contatos): uma negociação por item, com o mesmo
   * nome (e o nome do item depois do hífen), responsável, fonte, campanha, funil e etapa. O contato entra com a empresa dele.
   */
  lote?: { empresa?: { id: string; nome: string }; contato?: { id: string; nome: string } }[];
}

/** Props do diálogo de uma criação publicada por um módulo (aberto enquanto montado). */
export interface PropsCriacao {
  aoFechar: () => void;
  contexto: ContextoCriacao;
  /** O registro criado (o formulário de negociação usa para escolher a empresa ou o contato recém-criado). */
  aoCriar?: (r: { id: string; nome?: string }) => void;
}

/**
 * Ajustes do CRM 27/10 (C6, K2): uma criação que o módulo publica para o botão flutuante e o menu "Criar" da casca nova
 * (negociação, empresa, contato, tarefa, funil). Ou abre o próprio diálogo (`componente`), ou leva a uma página (`destino`).
 * Aparece com o módulo instalado e a permissão; `rotulo` é uma chave do i18n do núcleo (`criacao.itens.<chave>`).
 */
export interface CriacaoWeb {
  chave: string;
  rotulo: string;
  icone: LucideIcon;
  permissao: string;
  subsistemas: readonly string[];
  posicao: number;
  componente?: ComponentType<PropsCriacao>;
  destino?: string;
}

/** Contrato que cada módulo exporta em apps/web/src/modules/<slug>/index.tsx. */
export interface ModuloWeb {
  slug: string;
  menu: MenuPrincipal;
  /** Ordem dentro do menu (quando o menu agrupa vários módulos). */
  ordem: number;
  abas: AbaWeb[];
  rotasExtras?: RotaExtraWeb[];
  i18n: { pt: Record<string, unknown>; en: Record<string, unknown>; es: Record<string, unknown> };
  /**
   * Fase 2 (W15): selo do item do menu principal (não lidas do chat da equipe no "Comunicação"). O componente busca o
   * próprio número e não renderiza nada quando é zero; `compacto` é o trilho de ícones do menu recolhido. Fase 2 (W17,
   * K11): `noBotao` é o canto do botão do menu no cabeçalho do celular (o mesmo número, sobre o ícone).
   */
  seloMenu?: ComponentType<{ compacto?: boolean; noBotao?: boolean }>;
  /**
   * Fase 2 (W21, K7): ação de uma linha dos cards em lista do Início que este módulo publica, pela chave do indicador
   * (a baixa rápida em "Próximos vencimentos"). O componente decide sozinho se aparece (permissão) e não renderiza nada
   * quando a pessoa não pode agir; `nome` é o texto da linha já pelo modo discreto (nome do parceiro), `rotulo` o código do tipo.
   */
  acoesItemInicio?: Record<string, ComponentType<AcaoItemInicioProps>>;
  /**
   * Fase 2 (W23, K11): ação do módulo no cabeçalho do chatter de qualquer registro (ex.: "Criar tarefa" do Tarefas). Só
   * aparece com o módulo instalado; o componente decide a permissão e não renderiza nada quando não vale.
   */
  acaoNoRegistro?: ComponentType<PropsAcaoNoRegistro>;
  /**
   * Portal do Cliente (Onda 1): cartão do módulo na ficha do CLIENTE (Cadastros), com o módulo instalado. O componente decide a
   * permissão e não renderiza nada quando a pessoa não pode ver. Ex.: a aba Portal (acessos, documentos, recebidos e trilha).
   */
  cartaoNaFichaCliente?: ComponentType<PropsCartaoNaFicha>;
  /**
   * Ajustes do CRM (27/10, F1): o formulário de contato ou empresa do Cadastros (criar ou editar) aberto por outro módulo sem
   * sair da tela (a ficha da negociação: editar o contato, criar contato novo, criar empresa). O componente confere a permissão.
   */
  formularioParceiro?: ComponentType<PropsFormularioParceiro>;
  /**
   * Lote de 19/10 (item 16): o diálogo de convite pelo tipo (Admin > Usuários e papéis > Convidar), publicado pelo Admin para
   * outro módulo abrir já preenchido (Pessoas: "Convidar para o sistema" na ficha do funcionário sem acesso).
   */
  dialogoConvite?: ComponentType<PropsDialogoConvite>;
  /**
   * "Meus dados" (21/10): aba de Minha conta (`/conta/meus-dados`) publicada pelo módulo, com ele instalado. Pessoas: a
   * própria ficha de funcionário, só leitura.
   */
  abaMinhaConta?: ComponentType;
  /**
   * Lista 30/10 B (item 34): cartão que o módulo põe em Minha conta > Perfil, com ele instalado (Agenda: o Google Agenda da
   * própria pessoa).
   */
  cartaoMinhaConta?: ComponentType;
  /**
   * CRM no novo desenho, lote 6 (N8): grupos que o módulo põe na busca da barra superior (negociações, empresas, contatos).
   * Cada grupo só aparece a quem tem a permissão; o título é uma chave do i18n do módulo.
   */
  buscaGlobal?: FonteBuscaGlobal[];
  /** CRM no novo desenho, lote 6 (N9): perguntas que o módulo responde no Copiloto (respostas simuladas, sem IA nem custo). */
  copiloto?: PerguntaCopiloto[];
  /** CRM no novo desenho (Fase 2): páginas próprias do sub-sistema na casca nova (`PaginaCascaWeb`). */
  paginasCasca?: PaginaCascaWeb[];
  /**
   * Ajustes do CRM 27/10 (C2, C15, K3): botão "Atalho de tarefas" (ícone de calendário que abre o drawer das tarefas do dia),
   * publicado pelo Pipeline para os cabeçalhos das páginas do CRM. Outro módulo usa `<AtalhoDeTarefas />` de
   * `nucleo/atalho-tarefas`; o componente decide a permissão.
   */
  atalhoTarefas?: ComponentType;
  /** Ajustes do CRM 27/10 (C6, K2): criações do botão flutuante e do menu "Criar". */
  criacoes?: CriacaoWeb[];
  /**
   * Lote crm7: os campos personalizados de uma entidade (lista e gaveta de criar e editar), publicados pelo Configurações para
   * as Configurações do CRM ("Configurar campos de cadastro", com as abas por entidade). O componente confere a permissão.
   */
  camposPorEntidade?: ComponentType<PropsCamposPorEntidade>;
  /** Lista de 30/10 (B), item 24: o pop-up de criar um campo personalizado, para os cadastros dos outros módulos. */
  criarCampo?: ComponentType<PropsCriarCampo>;
}

/** Lista de 30/10 (B), item 24: a entidade do campo novo; `aoCriar` roda depois de gravar (o pop-up já fechou). */
export interface PropsCriarCampo {
  entidade: string;
  /** Lista de 03/11, item 7 (rl0311-6): aberto de um cadastro que divide a entidade (empresa ou contato), o campo nasce só nele. */
  abaFixa?: { campo: string; valor: string; aba: string };
  aberto: boolean;
  aoFechar: () => void;
  aoCriar?: () => void;
}

/** Lote crm7: a entidade fixa (sem o seletor nem o histórico) e o cabeçalho de quem usa, que recebe o botão "Criar campo". */
export interface PropsCamposPorEntidade {
  entidadeFixa: string;
  cabecalho?: (novo: ReactNode) => ReactNode;
  /** Lista de 03/11, item 7: a aba fixa a condição de exibição dos campos (Empresas e Contatos, o `tipo` do parceiro); `aba` é o rótulo traduzido. */
  condicaoAba?: { campo: string; valor: string; aba: string };
}

/** Lote 6 (N8): um resultado da busca global. `detalhe` é a linha de apoio (etapa, e-mail); `link`, a rota interna. */
export interface ItemBuscaGlobal {
  id: string;
  rotulo: string;
  detalhe?: string;
  link: string;
}

/**
 * Lista de 30/10 (item 10, busca em todo o sistema): um grupo da busca da barra superior. O servidor (`GET /busca-global`)
 * roda a rota de listagem que o módulo declara com `r.buscaGlobal` (mesma chave) e devolve as linhas cruas; aqui cada linha
 * vira um item (rótulo e detalhe sempre pelos formatadores do modo discreto) com o destino na página do módulo.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FonteBuscaGlobal<L = any> {
  /** A mesma chave da fonte no servidor (`<slug>.<chave>`). */
  chave: string;
  permissao: string;
  /**
   * Review rval1-1 (VIS-M2): a página (`<slug>.<aba>`) cujos registros o grupo mostra. Página negada (desmarcada no papel
   * ou retirada da pessoa): o grupo sai da busca, como a página sai do menu. O sub-sistema não conta (só arruma o menu).
   */
  pagina?: string;
  /**
   * Review rl30-1 (SEG-M2): o grupo que cobre várias páginas (o Conversas, uma por canal) sai só quando todas estão negadas;
   * o servidor traz só os registros das páginas liberadas.
   */
  paginas?: string[];
  /** Chave do i18n do módulo com o nome do grupo. */
  grupo: string;
  /** Ordem do grupo na lista de resultados (menor primeiro). */
  posicao: number;
  /** Ícone do grupo (o mesmo da página de destino, quando ela tem um). */
  icone: LucideIcon;
  /** Uma linha da listagem do módulo vira um item; `t` é o do módulo e `fuso` o do workspace. */
  item(linha: L, t: TFunction, fuso?: string): ItemBuscaGlobal;
  /** "Ver todos": a página da lista já filtrada pelo termo, só quando ela lê `?busca=` (ausente: sem o link). */
  verTodos?(termo: string): string | null;
}

/** Lote 6 (N9): resposta estruturada (o núcleo desenha; nada de HTML vindo do módulo). */
export interface RespostaCopiloto {
  texto: string;
  itens?: { rotulo: string; valor?: string; link?: string }[];
  link?: { rotulo: string; para: string };
}

export interface PerguntaCopiloto {
  chave: string;
  categoria: 'duvidas' | 'analise';
  permissao: string;
  /** Chave do i18n do módulo com a pergunta sugerida. */
  pergunta: string;
  /** Casa com a pergunta digitada (sem acento e em minúsculas). */
  casa: RegExp;
  responder(t: TFunction): Promise<RespostaCopiloto>;
}

export interface PropsDialogoConvite {
  aberto: boolean;
  aoFechar: () => void;
  /** Abre no tipo "Funcionário" com esta pessoa escolhida. */
  funcionario?: { id: string; nome: string };
}

export interface PropsFormularioParceiro {
  tipo: 'pessoa' | 'empresa';
  aberto: boolean;
  aoFechar: () => void;
  /** Cadastro a editar; sem ele, cria. */
  id?: string | null;
  /** Empresa do contato novo (já preenchida). */
  empresa?: { id: string; nome: string } | null;
  aoSalvar?: (id: string) => void;
}

export interface PropsCartaoNaFicha {
  parceiroId: string;
  nome: string;
  /** Contatos e sócios já cadastrados (para convidar sem digitar o e-mail). */
  contatos: { id: string; nome: string; email: string | null }[];
}

export interface AcaoItemInicioProps {
  itemId: string;
  nome: string;
  rotulo?: string;
}

/** O módulo como aparece num menu: só as abas daquele menu; `projetado` quando o menu não é o do módulo. */
export interface ModuloNoMenu extends ModuloWeb {
  projetado: boolean;
}

const encontrados = import.meta.glob<{ default: ModuloWeb }>('../modules/*/index.tsx', { eager: true });

export const modulosWeb: ModuloWeb[] = Object.values(encontrados)
  .map((m) => m.default)
  .sort((a, b) => a.ordem - b.ordem);

export const moduloWeb = (slug: string) => modulosWeb.find((m) => m.slug === slug);

/** Menu onde a aba mora: o da projeção, senão o do módulo. */
export const menuDaAba = (m: Pick<ModuloWeb, 'menu'>, a: Pick<AbaWeb, 'menu'>): string => a.menu ?? m.menu;

export interface Acesso {
  instalado(slug: string): boolean;
  pode(permissao: string): boolean;
  /** Fase 2 (W8): modo de operação do workspace; ausente, a aba que declara modo não aparece. */
  modo?(chave: string): string | undefined;
  /** Fase 2 (W37): administrador da plataforma (abas `plataforma`). */
  plataforma?: boolean;
  /** Lote 19: vê todas as empresas com o consolidado (abas `todasAsEmpresas`). */
  todasAsEmpresas?: boolean;
  /** U15 F5: modo simples da pessoa (esconde as abas avançadas da navegação). */
  modoSimples?: boolean;
  /**
   * D1 (sub-sistemas): a aba aparece na NAVEGAÇÃO do sub-sistema atual (`paginaNoSubsistema`). Ausente: todas (sem a
   * configuração ligada, a navegação de sempre). Só filtra menu e barras: a rota continua abrindo pelo link.
   */
  naNavegacao?(slug: string, aba: string): boolean;
  /**
   * Val1 (papel por sub-sistema, 28/10): a página não foi desmarcada no papel da pessoa nem retirada dela. A aba negada some
   * do menu E da rota (`modulosDoMenu` a tira do módulo). Ausente: todas.
   */
  paginaLiberada?(slug: string, aba: string): boolean;
}

/** Aba visível na navegação: permissão, modo de operação e, com sub-sistemas ligados, o sub-sistema atual. */
function visivelNaNavegacao(slug: string, a: AbaWeb, acesso: Acesso): boolean {
  if (acesso.paginaLiberada && !acesso.paginaLiberada(slug, a.chave)) return false;
  return abaVisivel(a, { pode: (p) => acesso.pode(p), modo: (c) => acesso.modo?.(c), plataforma: acesso.plataforma, todasAsEmpresas: acesso.todasAsEmpresas, modoSimples: acesso.modoSimples }) && (acesso.naNavegacao?.(slug, a.chave) ?? true);
}

/**
 * Fase 2 (W6, L1): módulos de um menu, compostos com as abas projetadas. Entra o módulo instalado com ao menos uma aba
 * daquele menu que a pessoa pode ver (a permissão é a da própria aba e, fase 2 W8, o modo de operação). Os módulos do próprio menu vêm primeiro (na
 * ordem deles); os que só projetam abas vêm depois, por ordem e slug. A rota extra (ficha) só vale na casa do módulo.
 */
export function modulosDoMenu(menu: string, acesso: Acesso, lista: ModuloWeb[] = modulosWeb): ModuloNoMenu[] {
  const vistos = lista
    .filter((m) => acesso.instalado(m.slug))
    .map<ModuloNoMenu>((m) => {
      const projetado = m.menu !== menu;
      // Val1: a página negada sai do módulo (sem menu e sem rota).
      return { ...m, projetado, abas: m.abas.filter((a) => menuDaAba(m, a) === menu && (acesso.paginaLiberada?.(m.slug, a.chave) ?? true)), rotasExtras: projetado ? undefined : m.rotasExtras };
    })
    // Fase 2 (W8): entra quem tem ao menos uma aba visível (permissão da aba e, quando ela declara, o modo de operação).
    // D1: e, com sub-sistemas ligados, que apareça no sub-sistema atual.
    .filter((m) => m.abas.some((a) => visivelNaNavegacao(m.slug, a, acesso)));
  const chave = (m: ModuloNoMenu) => [m.projetado ? 1 : 0, m.ordem] as const;
  return vistos.sort((a, b) => chave(a)[0] - chave(b)[0] || chave(a)[1] - chave(b)[1] || a.slug.localeCompare(b.slug));
}

/** Caminho web de uma aba, já na casa certa (projetada ou não). Null quando o módulo ou a aba não existem. */
export function caminhoDaAba(slug: string, chave: string, lista: ModuloWeb[] = modulosWeb): string | null {
  const m = lista.find((x) => x.slug === slug);
  const a = m?.abas.find((x) => x.chave === chave);
  return m && a ? `/${menuDaAba(m, a)}/${m.slug}/${a.chave}` : null;
}

/**
 * Rota antiga de uma aba que foi projetada em outro menu (`/financeiro/financeiro/categorias`): devolve a nova
 * (`/cadastros/financeiro/categorias`), com o resto do caminho; null quando a aba mora mesmo neste menu.
 */
export function destinoDaAbaProjetada(menu: string, slug: string, resto: string, lista: ModuloWeb[] = modulosWeb): string | null {
  const m = lista.find((x) => x.slug === slug);
  const [chave, ...depois] = resto.split('/').filter(Boolean);
  const a = chave ? m?.abas.find((x) => x.chave === chave) : undefined;
  // Fase 2 (W31): o módulo inteiro mudou de menu (Relatórios saiu do Financeiro): a raiz e a rota extra levam à casa nova.
  if (m && !a && m.menu !== menu && !m.abas.some((x) => menuDaAba(m, x) === menu)) return `/${m.menu}/${m.slug}${resto ? `/${resto.replace(/^\/+/, '')}` : ''}`;
  if (!m || !a || menuDaAba(m, a) === menu) return null;
  return `/${menuDaAba(m, a)}/${m.slug}/${[a.chave, ...depois].join('/')}`;
}

/** Fase 2 (W31): uma aba na navegação de um menu, com o módulo dono e o caminho. */
export interface AbaNoMenu {
  slug: string;
  aba: AbaWeb;
  para: string;
}

/** Fase 2 (W31): grupo de abas (aba de primeiro nível) de um menu agrupado. */
export interface GrupoNoMenu {
  chave: string;
  /** Grupo declarado em `NAVEGACAO_MENUS` (rótulo `navegacao.<menu>.<chave>`) ou o módulo (rótulo `<slug>:nome`). */
  declarado: boolean;
  abas: AbaNoMenu[];
}

/**
 * Fase 2 (W31): como o menu mostra as abas. `modulos`: abas de módulo em cima e as abas do módulo embaixo (o padrão).
 * `grupos`: grupos declarados em cima e as abas do grupo (de qualquer módulo) embaixo (Cadastros). `linha`: uma linha só
 * com as abas de todos os módulos do menu (Financeiro). Só entram as abas visíveis (`abaVisivel`), na `posicao`.
 */
export type NavegacaoDoMenu = { tipo: 'modulos'; modulos: ModuloNoMenu[] } | { tipo: 'grupos'; grupos: GrupoNoMenu[] } | { tipo: 'linha'; abas: AbaNoMenu[] };

/** Abas visíveis de um menu, de todos os módulos, na posição declarada (sem ela, na ordem dos módulos e do manifesto). */
function abasVisiveisDoMenu(menu: string, acesso: Acesso, lista: ModuloWeb[]): AbaNoMenu[] {
  const todas = modulosDoMenu(menu, acesso, lista).flatMap((m) => m.abas.filter((a) => visivelNaNavegacao(m.slug, a, acesso)).map((a) => ({ slug: m.slug, aba: a, para: `/${menu}/${m.slug}/${a.chave}` })));
  return todas.map((x, i) => ({ x, i })).sort((p, q) => (p.x.aba.posicao ?? 1000 + p.i) - (q.x.aba.posicao ?? 1000 + q.i)).map(({ x }) => x);
}

export function navegacaoDoMenu(menu: string, acesso: Acesso, lista: ModuloWeb[] = modulosWeb): NavegacaoDoMenu {
  const decl = NAVEGACAO_MENUS[menu as MenuPrincipal];
  if (!decl) return { tipo: 'modulos', modulos: modulosDoMenu(menu, acesso, lista) };
  const abas = abasVisiveisDoMenu(menu, acesso, lista);
  if (decl.tipo === 'linha') return { tipo: 'linha', abas };
  const grupos = new Map<string, GrupoNoMenu>(decl.grupos.map((g) => [g, { chave: g, declarado: true, abas: [] }]));
  for (const x of abas) {
    const chave = x.aba.grupo ?? x.slug;
    if (!grupos.has(chave)) grupos.set(chave, { chave, declarado: false, abas: [] });
    grupos.get(chave)!.abas.push(x);
  }
  return { tipo: 'grupos', grupos: [...grupos.values()].filter((g) => g.abas.length > 0) };
}

/** Fase 2 (W31): todas as abas visíveis do menu, na ordem da navegação (a primeira é o destino do item do menu lateral). */
export function abasDaNavegacao(n: NavegacaoDoMenu): AbaNoMenu[] {
  if (n.tipo === 'linha') return n.abas;
  if (n.tipo === 'grupos') return n.grupos.flatMap((g) => g.abas);
  return n.modulos.flatMap((m) => m.abas.map((a) => ({ slug: m.slug, aba: a, para: `/${menuDaAbaNoMenu(m)}/${m.slug}/${a.chave}` })));
}

const menuDaAbaNoMenu = (m: ModuloNoMenu) => (m.abas[0] ? menuDaAba(m, m.abas[0]) : m.menu);

/**
 * Fase 2 (W31): destino do item do menu lateral. No menu por módulos, a raiz do primeiro módulo (a `Secao` escolhe a
 * aba); nos agrupados e de linha única, a primeira aba da navegação. Null quando o menu não tem nada visível.
 */
export function destinoDoMenu(menu: string, acesso: Acesso, lista: ModuloWeb[] = modulosWeb): string | null {
  const n = navegacaoDoMenu(menu, acesso, lista);
  if (n.tipo === 'modulos') return n.modulos[0] ? `/${menu}/${n.modulos[0].slug}` : null;
  return abasDaNavegacao(n)[0]?.para ?? null;
}

/**
 * Review r23 spec (O1): destino de um redirecionamento da seção com a busca da URL de origem (link de indicador, de relatório
 * ou favorito com filtros). Vale para o caminho absoluto e para o relativo (a primeira aba do módulo).
 */
export const comBusca = (destino: string, busca: string) => `${destino}${busca && busca !== '?' ? (busca.startsWith('?') ? busca : `?${busca}`) : ''}`;

/** Uma página da casca com o módulo dono. */
export interface PaginaCascaNoModulo {
  slug: string;
  pagina: PaginaCascaWeb;
}

const podeAbrirPagina = (p: Pick<PaginaCascaWeb, 'permissao'>, pode: (permissao: string) => boolean) => (typeof p.permissao === 'string' ? pode(p.permissao) : p.permissao.some(pode));

/** CRM no novo desenho (Fase 2): todas as páginas da casca dos módulos instalados que a pessoa pode abrir (as rotas). */
export function paginasDaCasca(acesso: Pick<Acesso, 'instalado' | 'pode' | 'paginaLiberada'>, lista: ModuloWeb[] = modulosWeb): PaginaCascaNoModulo[] {
  // Val1: a página da casca que mostra uma aba negada (desmarcada no papel ou retirada da pessoa) também some.
  const liberada = (p: PaginaCascaWeb) => !p.pagina || (acesso.paginaLiberada?.(p.pagina.slice(0, p.pagina.indexOf('.')), p.pagina.slice(p.pagina.indexOf('.') + 1)) ?? true);
  return lista
    .filter((m) => acesso.instalado(m.slug))
    .flatMap((m) => (m.paginasCasca ?? []).filter((p) => podeAbrirPagina(p, acesso.pode) && liberada(p)).map((pagina) => ({ slug: m.slug, pagina })));
}

/**
 * As páginas da casca do sub-sistema atual, na `posicao` (os itens da barra lateral da casca nova). Val8: o sub-sistema de cada
 * uma vem da configuração (`cfg`, a efetiva; vazia: a divisão padrão).
 */
export function paginasDoSubsistema(acesso: Pick<Acesso, 'instalado' | 'pode' | 'paginaLiberada'>, sub: string | null, lista: ModuloWeb[] = modulosWeb, cfg: ConfigSubsistemas = {}): PaginaCascaNoModulo[] {
  if (!sub) return [];
  return paginasDaCasca(acesso, lista)
    .filter((x) => !x.pagina.semMenu && paginaNoSubsistema(cfg, sub, x.slug, abaDaCasca(x.pagina.chave)))
    .sort((a, b) => a.pagina.posicao - b.pagina.posicao || a.pagina.caminho.localeCompare(b.pagina.caminho));
}

/**
 * Rodada crm1 (SEG-M1): o módulo dono da tela aberta, para o log de erros mostrar o que estava preenchido só a quem tem
 * `<modulo>.ver`. Página da casca nova pelo caminho; tela de sempre (`/menu/modulo/aba`) pelo trecho que é módulo.
 */
export function moduloDaRota(caminho: string, lista: ModuloWeb[] = modulosWeb): string | null {
  const limpo = (caminho.split('?')[0] ?? '').replace(/^\/+|\/+$/g, '');
  for (const m of lista) for (const p of m.paginasCasca ?? []) if (limpo === p.caminho || limpo.startsWith(`${p.caminho}/`)) return m.slug;
  const [a, b] = limpo.split('/');
  if (b && lista.some((m) => m.slug === b)) return b;
  if (a && lista.some((m) => m.slug === a)) return a;
  return null;
}

export interface CriacaoNoModulo {
  slug: string;
  criacao: CriacaoWeb;
}

/** Ajustes do CRM 27/10 (C6): as criações do sub-sistema, dos módulos instalados e com a permissão, na `posicao`. */
export function criacoesDoSubsistema(acesso: Pick<Acesso, 'instalado' | 'pode'>, sub: string | null, lista: ModuloWeb[] = modulosWeb): CriacaoNoModulo[] {
  if (!sub) return [];
  return lista
    .filter((m) => acesso.instalado(m.slug))
    .flatMap((m) => (m.criacoes ?? []).filter((c) => c.subsistemas.includes(sub) && acesso.pode(c.permissao)).map((criacao) => ({ slug: m.slug, criacao })))
    .sort((a, b) => a.criacao.posicao - b.criacao.posicao || a.criacao.chave.localeCompare(b.criacao.chave));
}

/** A criação de uma chave (a empresa e o contato do formulário de negociação), se instalada e permitida. */
export function criacaoDaChave(acesso: Pick<Acesso, 'instalado' | 'pode'>, chave: string, lista: ModuloWeb[] = modulosWeb): CriacaoNoModulo | null {
  for (const m of lista) {
    if (!acesso.instalado(m.slug)) continue;
    const c = (m.criacoes ?? []).find((x) => x.chave === chave && acesso.pode(x.permissao));
    if (c) return { slug: m.slug, criacao: c };
  }
  return null;
}
